import { Button, ErrorAlert, FormError, TextInput, useTextForm } from '@lumlab/platform-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '@lumlab/platform-product/web';
import { agencyInput, type Agency, type AgencyInput } from '../contract/index.ts';

export function AgencyForm({
    onDone,
    endpoint,
    queryKey,
    agency,
}: {
    onDone: () => void;
    endpoint: string;
    queryKey: readonly unknown[];
    agency?: Agency;
}) {
    const { t } = useTranslation();
    const client = useQueryClient();
    const form = useTextForm({
        initialValues: { name: agency?.name ?? '', address: agency?.address ?? '' },
        validate: (values) => {
            const result = agencyInput.safeParse(values);
            return result.success
                ? {}
                : Object.fromEntries(
                      result.error.issues.map((issue) => [String(issue.path[0]), t('platform.errors.bad_request')]),
                  );
        },
    });
    const save = useMutation({
        mutationFn: (input: AgencyInput) =>
            apiRequest<Agency>(agency ? `${endpoint}/${agency.id}` : endpoint, {
                method: agency ? 'PATCH' : 'POST',
                body: input,
            }),
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey });
            onDone();
        },
    });
    return (
        <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
            <div className="flex flex-col gap-4">
                <FormError message={form.formError} />
                {save.error && <ErrorAlert error={save.error} />}
                <TextInput
                    label={t('tenders.agencies.name')}
                    required
                    maxLength={200}
                    {...form.getInputProps('name')}
                />
                <TextInput label={t('tenders.agencies.address')} maxLength={500} {...form.getInputProps('address')} />
                <div className="flex flex-wrap items-center gap-4 justify-end">
                    <Button variant="default" onClick={onDone}>
                        {t('tenders.agencies.cancel')}
                    </Button>
                    <Button type="submit" loading={save.isPending}>
                        {t('tenders.agencies.save')}
                    </Button>
                </div>
            </div>
        </form>
    );
}
